// src/encounter/encounter-chart-panel.tsx
// PROD-1986 — the encounter's Chart tab BODY.
//
// Flutter PA mounts the chart INSIDE the encounter screen, twice:
//   · pre-encounter  — encounter_screen.dart:1353 `chartViewMainPanel` (patient-summary band +
//     ChartMainView), selected as tab 1 of `tabViewInitial` (:1459-1490).
//   · in-encounter   — encounter_screen.dart:1049-1057 `tabViewContent case "Chart"` → the same
//     ChartMainView, swapped into the TabBarView under the patient-summary banner.
// Both pass patientId + practiceId + orgId + **encounterId**, and leave `showDemogTab` at its
// default `true`. The patient-edit mount (create_patient_screen.dart:1170) is the DIFFERENT one:
// `showDemogTab: false`, no encounterId — patient-scoped.
//
// This panel is the React twin of that encounter mount: it composes the SHIPPED `ChartTabBody`
// (PROD-735/736/737/738) rather than a second chart surface, and owns the one fetch the chart
// needs (`usePatient` → the `Patient` shape ChartTabBody takes — the same hook patient-edit uses).
// Mounting it only while the Chart tab is active keeps that fetch off the encounter's hot path.
//
// KNOWN DELTA (declared, not faked): Flutter's encounter mount also shows the chart's **Demog**
// inner tab. `ChartTabBody` has no Demog tab — it was built for the `showDemogTab: false`
// patient-edit mount, and the React demographics editor (`PersonalDetailsTab`) is bound to the
// patient-edit shell context (`usePatientEditShell`), so it cannot be dropped in here. Porting it
// is its own surface; a stub would be anti-fiction. See PROD-1986.
import { ChartTabBody } from '@/patients/components/chart/chart-tab-body';
import { usePatient } from '@/patients/services/use-patient';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';
import { UnsavedChangesProvider } from '@/shared/ui/components/unsaved-changes';

export interface EncounterChartPanelProps {
  patientId: string | null | undefined;
  /** The encounter this chart is opened from. It STAMPS documents added here onto the
   *  encounter — it does NOT scope what the Docs tab lists. Flutter's chart Docs tab
   *  fetches patient-wide on both mounts (`chart/tab_widgets/doc_tab.dart:619-638` sends
   *  no encounterId) and uses the id only for the add/save path (:222,554,679). Listing
   *  only this encounter's docs is the encounter's own Docs STEP, not the chart. */
  encounterId?: string | null;
  /** Draw the chart's own patient panel on the left (default). The started frame owns a left panel on every tab and passes false. */
  leftPanel?: boolean;
}

export function EncounterChartPanel({ patientId, encounterId, leftPanel = true }: EncounterChartPanelProps) {
  const { data: patient, isLoading } = usePatient(patientId);

  if (isLoading && !patient) {
    return (
      <div className="p-8 text-text-secondary text-size-sm">
        <QuietLoading label="Loading chart…" />
      </div>
    );
  }

  return (
    // The "Unsaved changes" bar sits in a reserved slot above the scroller, directly under the page header; it
    // appears when a section of the chart (Demographics, Emergency contacts, Health insurance) has edits to save.
    <div className="flex-1 min-w-0 min-h-0 flex flex-col">
    <UnsavedChangesProvider>
    {/* The patient record's chart, the same component path (Antonin, 19 Oct): Summary cards, the same sections, caps and
        fitted tables, the patient panel on the left; the same sticky index and one scrolling page as the record's Chart tab
        (export 23 Oct, "Patient · Chart"; Ahmed, 23 Oct: the encounter's chart is a full page, not a narrow column). With ?x=chart-timeline its lead tab is the patient's Timeline (Andrew, 4 Nov); the header's own "Timeline" tab is the visit's care pathway, another strip.
        The chart scrolls itself (panel and page each), so no scroller here.
        PROD-1988: `demographics` ON — Flutter's ENCOUNTER mount leaves `showDemogTab` at its default true (only the
        patient-edit mount passes false), so the encounter chart carries Demographics exactly as it does in the Flutter app. */}
    <div className="flex-1 min-w-0 min-h-0" data-slot="chart-scroller">
      <ChartTabBody
        patient={patient ?? null}
        encounterId={encounterId}
        visibility={{ demographics: true }}
        onePage
        layout="index"
        leftPanel={leftPanel}
      />
    </div>
    </UnsavedChangesProvider>
    </div>
  );
}
